import {
  IUserExtraSubscription,
  IUserSubscriptionsInfo,
} from 'models/commonApi.model'

import {
  ExtraSubscription,
  ExtraSubscriptionUIStatus,
  HiddenInAppProduct,
  SecondarySubscription,
  SubscriptionStatus,
  SubscriptionUIStatus,
} from 'root-constants'

import { ActiveSubscriptionDetails } from './components/ActiveSubscriptionDetails'
import { CanceledSubscriptionDetails } from './components/CanceledSubscriptionDetails'
import { PausedSubscriptionDetails } from './components/PausedSubscriptionDetails'
import { TrialSubscriptionDetails } from './components/TrialSubscriptionDetails'
import { WithdrawnSubscriptionDetails } from './components/WithdrawnSubscriptionDetails'

export type TSubscriptionDetailsProps = {
  extra?: IUserExtraSubscription
  secondarySubscription?: IUserSubscriptionsInfo
}

export const CANCELLED_STATUSES = [
  SubscriptionStatus.CANCELLED,
  SubscriptionStatus.NOT_ACTIVE,
  SubscriptionStatus.DEACTIVATED,
]

export const STATUSES_TEXT_MAP = {
  [SubscriptionUIStatus.ACTIVE]: 'subscription.statuses.active',
  [SubscriptionUIStatus.CANCELED]: 'subscription.statuses.canceled',
  [SubscriptionUIStatus.TRIAL]: 'subscription.statuses.trial',
  [SubscriptionUIStatus.PAUSED]: 'subscription.statuses.paused',
  [SubscriptionUIStatus.WITHDRAWN]: 'subscription.statuses.withdrawn',
  [ExtraSubscriptionUIStatus.NOT_ACTIVE]: 'subscription.statuses.canceled',
}

export const SMALLER_BUTTON_FONT_SIZE_LOCALES = ['fr', 'de', 'it']

export const SUBSCRIPTION_DETAILS_MAP = {
  [SubscriptionUIStatus.ACTIVE]: ActiveSubscriptionDetails,
  [SubscriptionUIStatus.CANCELED]: CanceledSubscriptionDetails,
  [SubscriptionUIStatus.TRIAL]: TrialSubscriptionDetails,
  [SubscriptionUIStatus.PAUSED]: PausedSubscriptionDetails,
  [SubscriptionUIStatus.WITHDRAWN]: WithdrawnSubscriptionDetails,
}

export const EXTRA_SUBSCRIPTION_DETAILS_MAP = {
  ...SUBSCRIPTION_DETAILS_MAP,
  [ExtraSubscriptionUIStatus.NOT_ACTIVE]: CanceledSubscriptionDetails,
}

export const EXTRA_PRODUCT_NAMES_MAP: Record<ExtraSubscription, string> = {
  [ExtraSubscription.WEIGHT_LOSS_BUNDLE]:
    'subscription.extras.weightLossBundle',
  [ExtraSubscription.WEIGHT_LOSS_BUNDLE_FREE]:
    'subscription.extras.weightLossBundle',
  [ExtraSubscription.EXTRA_COURSES]: 'subscription.extras.weightLossBundle',
  [ExtraSubscription.SEVEN_DAY]: 'subscription.extras.weightLossBundle',
  [ExtraSubscription.FLAT_TUMMY]: 'subscription.extras.flatTummy',
  [ExtraSubscription.ULTIMATE_TONING_PILATES]:
    'subscription.extras.ultimateToningPilates',
  [ExtraSubscription.SUPERCHARGE_YOURSELF]:
    'subscription.extras.superchargeYourself',
  [ExtraSubscription.FEMININITY_PROGRAM]:
    'subscription.extras.femininityProgram',
  [ExtraSubscription.LUVLY_PROGRAM]: 'subscription.extras.luvlyProgram',
  [ExtraSubscription.INTENSE_CALORIE_BURNING_DANCES]:
    'subscription.extras.intenseCalorieBurningDances',
  [ExtraSubscription.CHIN_FACE_LIFTING]:
    'subscription.extras.doubleChinAndFaceLifting',
  [ExtraSubscription.CHIN_SHAPER]: 'subscription.extras.doubleChin',
  [ExtraSubscription.FACE_LIFTING]: 'subscription.extras.faceLifting',
  [ExtraSubscription.HAIR_GUIDE]: 'subscription.extras.hairCare',
  [ExtraSubscription.GIFT_CARD]: 'subscription.extras.giftCard',
  [ExtraSubscription.FITNESS_SELFCARE]: 'subscription.extras.fitnessSelfCare',
  [ExtraSubscription.FITNESS]: 'subscription.extras.dreamBody',
  [ExtraSubscription.SELFCARE]: 'subscription.extras.selfCareGuide',
  [ExtraSubscription.SIMPLE_CHANGES]: 'subscription.extras.simpleChanges',
  [ExtraSubscription.SUGAR_FREE_DESSERTS]:
    'subscription.extras.sugarFreeDesserts',
  [ExtraSubscription.WEIGHT_LOSS_GUIDES]:
    'subscription.extras.weightLossGuides',
  [ExtraSubscription.DIET]: 'subscription.extras.diet',
  [ExtraSubscription.HORMONAL_BALANCE]: 'subscription.extras.diet',
  [ExtraSubscription.COOKBOOK]: 'subscription.extras.cookbook',
  [ExtraSubscription.COOKBOOK_PLUS_DIET]:
    'subscription.extras.cookbookPlusDiet',
  [ExtraSubscription.BUBBLE_BUTT]: 'subscription.extras.bubbleButt',
  [ExtraSubscription.BURN_BELLY_FAT]: 'subscription.extras.burnBellyFat',
  [ExtraSubscription.SENIOR_FITNESS]: 'subscription.extras.seniorFitness',
  [ExtraSubscription.FITNESS_AND_BELLY]:
    'subscription.extras.seniorFitnessAndBelly',
  [ExtraSubscription.BUTT_AND_BELLY]: 'subscription.extras.buttAndBelly',
  [ExtraSubscription.LYMPH_DRAINAGE]: 'subscription.extras.lymphDrainage',
  [ExtraSubscription.POSTURE_FITNESS]: 'subscription.extras.postureFitness',
  [ExtraSubscription.LYMPH_DRAINAGE_AND_POSTURE]:
    'subscription.extras.lymphDrainageAndPosture',
  [ExtraSubscription.SPEAKING_RESET]: 'subscription.extras.speakingReset',
  [ExtraSubscription.REAL_CONNECTION]: 'subscription.extras.realConnection',
}

export const SECONDARY_SUBSCRIPTION_NAMES_MAP: Record<
  SecondarySubscription,
  string
> = {
  [SecondarySubscription.LUVLY_COACHING]:
    'subscription.secondaries.luvlyCoaching',
  [SecondarySubscription.SUPERCHARGE_YOURSELF]:
    'subscription.programTitles.superchargeYourself',
  [SecondarySubscription.FEMININITY_PROGRAM]:
    'subscription.programTitles.unleashYourFemininity',
  [SecondarySubscription.FLAT_TUMMY]: 'subscription.programTitles.flatTummy',
  [SecondarySubscription.LUVLY_PROGRAM]: 'subscription.programTitles.faceYoga',
  [SecondarySubscription.INTENSE_CALORIE_BURNING]:
    'subscription.programTitles.intenseDances',
  [SecondarySubscription.TONING_PILATES]:
    'subscription.programTitles.ultimateToningPilates',
}

export const FREE_EXTRA_PROGRAMS = [
  ExtraSubscription.WEIGHT_LOSS_BUNDLE_FREE, // dancebit
  ExtraSubscription.SEVEN_DAY, // dancebit
]

// agreed with Marketing team to hide this purchases
export const HIDDEN_INAPP_PRODUCTS: string[] = [
  HiddenInAppProduct.GUASHA_AND_ROLLER,
  HiddenInAppProduct.GUASHA,
  HiddenInAppProduct.ROLLER,
]
